import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Tracks user inactivity and calls onTimeout when the user has been idle
 * for `timeoutMinutes` minutes.
 *
 * A warning is shown 2 minutes before the timeout fires so the user has a
 * chance to stay logged in.
 *
 * Activity events (mouse, keyboard, touch, scroll) reset the idle clock.
 *
 * All AcuityFlo tabs share ONE login, and a timeout in any tab logs every tab
 * out. So activity is shared: each tab stamps 'aflo-activity' in localStorage
 * (throttled) and every tab counts the newest stamp — working in one tab keeps
 * a board left open in another tab from signing you out.
 */
const ACTIVITY_KEY = 'aflo-activity';
const SHARE_EVERY_MS = 15 * 1000;

const sharedActivity = () => {
  try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
};

export function useSessionTimeout({ onTimeout, timeoutMinutes = 180 } = {}) {
  const WARNING_SECONDS = 2 * 60; // show warning 2 min before auto-logout

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);

  const lastActivityRef = useRef(Date.now());
  const timedOutRef = useRef(false);

  // Call this when the user clicks "Stay logged in"
  const stayLoggedIn = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setSecondsLeft(WARNING_SECONDS);
  }, [WARNING_SECONDS]);

  useEffect(() => {
    const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
    const WARNING_MS = WARNING_SECONDS * 1000;

    // ── Activity listeners ─────────────────────────────────────────────────
    let lastShared = 0;
    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      // Tell our other tabs (throttled — localStorage writes are synchronous).
      if (now - lastShared > SHARE_EVERY_MS) {
        lastShared = now;
        try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* storage blocked */ }
      }
    };
    // Scrolling counts: reading down a long board with a trackpad moves no mouse.
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'click', 'wheel'];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    // Element scrolls don't bubble — catch them on the way down instead.
    window.addEventListener('scroll', onActivity, { passive: true, capture: true });
    // Activity in ANOTHER tab of ours (other board tabs, and the in-browser
    // document editor while the user types) counts too — otherwise this tab
    // idles out and its logout clears the shared token under the busy tab.
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY) lastActivityRef.current = Math.max(lastActivityRef.current, Number(e.newValue) || 0);
    };
    window.addEventListener('storage', onStorage);

    // ── Tick every second ──────────────────────────────────────────────────
    const interval = setInterval(() => {
      if (timedOutRef.current) return;

      // Re-read the shared stamp too, in case a storage event was missed
      // while this tab was in the background.
      lastActivityRef.current = Math.max(lastActivityRef.current, sharedActivity());
      const idle = Date.now() - lastActivityRef.current;
      const remaining = TIMEOUT_MS - idle;

      if (remaining <= 0) {
        // Hard timeout reached
        timedOutRef.current = true;
        clearInterval(interval);
        setShowWarning(false);
        onTimeout?.();
        return;
      }

      if (remaining <= WARNING_MS) {
        // In the warning window
        setSecondsLeft(Math.ceil(remaining / 1000));
        setShowWarning(true);
      } else {
        // Outside warning window — hide modal if it was showing
        setShowWarning(false);
        setSecondsLeft(WARNING_SECONDS);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener('scroll', onActivity, { capture: true });
      window.removeEventListener('storage', onStorage);
    };
  }, [timeoutMinutes, onTimeout, WARNING_SECONDS]);

  return { showWarning, secondsLeft, stayLoggedIn };
}
